import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import './DepartmentCourses.css';
import ClearFilter from '../../components/ClearFilter';
import course1 from '../../images/course1.jpg';
import course2 from '../../images/course2.jpg';
import course3 from '../../images/course3.jpg';
import Statistics from '../../images/Statistics.jpg';
import Sqlimage from '../../images/Sqlimage.jpg';
import salesforce from '../../images/salesforce.png';
import productengineeringandservices from '../../images/productengineeringandservices.png';
import H1BSTAFF from '../../images/H1BSTAFF.png';
import erp from '../../images/erp.png';
import entierpriseandarchitecture from '../../images/entierpriseandarchitecture.png';
import datascienceandai from '../../images/datascienceandai.png';
import machinelearning from '../../images/machinelearning.jpg';
import azure from '../../images/azure.jpg';
import aws from '../../images/aws.jpg';
import airflow from '../../images/airflow.jpg';
import dbt from '../../images/dbt.jpg';
import deeplearning from '../../images/deeplearning.jpg';
import kafka from '../../images/kafka.jpg';
import gcp from '../../images/gcp.jpg';
import tableau from '../../images/tableau.jpg';
import pyspark from '../../images/pyspark.jpg';
import r from '../../images/r.jpg';
import matplotlib from '../../images/matplotlib.jpg';
import pandas from '../../images/pandas.jpg';
import hadoop from '../../images/hadoop.jpg';
import numpy from '../../images/numpy.jpg';
import oci from '../../images/oci.jpg';
import automation from '../../images/automation.jpg';
import springboot from '../../images/springboot.jpg';
import react from '../../images/react.jpg';
import mulesoft from '../../images/mulesoft.jpg';
import kubernetes from '../../images/kubernetes.png';
import java from '../../images/java.jpg';
import javascript from '../../images/javascript.jpg';
import django from '../../images/django.jpg';
import angular from '../../images/angular.jpg';
import useDepartments from '../../hooks/useDepartments';
import useCourses from '../../hooks/useCourses';
import { apiFetch } from '../../utils/api';
import TrainingVideos from '../../components/TrainingVideos';

const DepartmentCourses = () => {
  const { departmentName } = useParams();
  const navigate = useNavigate();
  const { departments } = useDepartments();
  const { courses: allCourses, loading: coursesLoading, error: coursesError } = useCourses();
  const [sortOrder, setSortOrder] = useState('alphabetical');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedLevel, setSelectedLevel] = useState('All Levels');
  const [searchQuery, setSearchQuery] = useState('');

  // ⭐ Track enrolled course IDs
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [enrolledLoading, setEnrolledLoading] = useState(true);

  // Helper: show snackbar-style popup (reuses your inline style approach)
  const showSnackbar = (message, backgroundColor = '#4CAF50') => {
    const snackbar = document.createElement('div');
    snackbar.textContent = message;
    snackbar.style.cssText =
      `position: fixed;
       top: 20px;
       left: 50%;
       transform: translateX(-50%);
       background-color: ${backgroundColor};
       color: white;
       padding: 12px 24px;
       border-radius: 4px;
       z-index: 1000;`;
    document.body.appendChild(snackbar);
    setTimeout(() => snackbar.remove(), 3000);
  };

  // 🔹 On mount: fetch already enrolled courses so that initial UI knows what to show
  useEffect(() => {
    const fetchEnrolledCourses = async () => {
      setEnrolledLoading(true);
      try {
        const userEmail = localStorage.getItem('userEmail');
        if (!userEmail) return;

        const userInfo = await apiFetch(`${window._env_.API_BASE_URL}/users/info?email=${userEmail}`);
        const userId = userInfo.user_id;

        const enrolledList = await apiFetch(`${window._env_.API_BASE_URL}/users/${userId}/courses`);

        // store only the course IDs
        const enrolledIds = enrolledList.map(c => c.course_id);
        setEnrolledCourses(enrolledIds);
      } catch (err) {
        console.error('Error fetching enrolled courses:', err);
      } finally {
        setEnrolledLoading(false);
      }
    };

    fetchEnrolledCourses();
  }, []);

  // ✅ ENROLL
  const handleEnroll = async (courseId) => {
    try {
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) {
        navigate('/login');
        return;
      }

      const userInfo = await apiFetch(`${window._env_.API_BASE_URL}/users/info?email=${userEmail}`);
      const userId = userInfo.user_id;

      try {
        await apiFetch(`${window._env_.API_BASE_URL}/users/${userId}/enroll`, {
          method: 'POST',
          body: JSON.stringify({ course_id: courseId }),
        });
      } catch (error) {
        if (error.message && error.message.includes('already enrolled')) {
          showSnackbar('Already enrolled in the course', '#f44336');
        } else {
          alert(`Enrollment failed: ${error.message}`);
        }
        return;
      }

      // Update local state so UI switches to "Unenroll"
      setEnrolledCourses(prev => (prev.includes(courseId) ? prev : [...prev, courseId]));

      // Success snackbar + navigate to course videos (your original behavior)
      const snackbar = document.createElement('div');
      snackbar.textContent = 'Successfully enrolled in the course';
      snackbar.style.cssText =
        'position: fixed; top: 20px; left: 50%; transform: translateX(-50%); background-color: #4CAF50; color: white; padding: 12px 24px; border-radius: 4px; z-index: 1000;';
      document.body.appendChild(snackbar);

      setTimeout(() => {
        snackbar.remove();
        navigate('/course-videos', { state: { department: departmentName, courseId } });
      }, 3000);
    } catch (error) {
      console.error('Enrollment error:', error);
      alert('An error occurred during enrollment. Please try again.');
    }
  };

  // ✅ UNENROLL
  const handleUnenroll = async (courseId) => {
    try {
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) {
        navigate('/login');
        return;
      }

      const userInfo = await apiFetch(`${window._env_.API_BASE_URL}/users/info?email=${userEmail}`);
      const userId = userInfo.user_id;

      await apiFetch(
        `${window._env_.API_BASE_URL}/users/unenroll?user_id=${userId}&course_id=${courseId}`,
        { method: 'DELETE' }
      );

      // update local state
      setEnrolledCourses(prev => prev.filter(id => id !== courseId));

      showSnackbar('Successfully unenrolled from the course', '#f44336');
    } catch (error) {
      console.error('Unenroll error:', error);
      alert('An error occurred while unenrolling. Please try again.');
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All Categories');
    setSelectedLevel('All Levels');
  };

  // BTG-specific categories
  const BTG_SPECIFIC_CATEGORIES = [
    'Automated SDLC Life Cycle',
    'Mastering Prompt Engineering',
    'Security,Privacy,and Governance'
  ];

  // Find the current department data
  const currentDepartment = departments.find(dept => dept.name === departmentName);

  // Filter courses for the current department
  const departmentCourses = useMemo(() => {
    return allCourses.filter(course =>
      course.department === departmentName
    );
  }, [allCourses, departmentName]);

  // Get unique categories and levels
  const baseCategories = ['All Categories', ...new Set(departmentCourses.map(course => course.category))];

  const categories = departmentName === 'Business Technology Group'
    ? [...new Set([...baseCategories, ...BTG_SPECIFIC_CATEGORIES])]
    : baseCategories;

  const levels = ['All Levels', ...new Set(departmentCourses.map(course => course.level))];

  // Check if selected category is a BTG-specific category
  const isBTGCategory = departmentName === 'Business Technology Group' &&
    BTG_SPECIFIC_CATEGORIES.includes(selectedCategory);

  // Filter + sort
  const filteredAndSortedCourses = useMemo(() => {
    let filtered = [...departmentCourses];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(course =>
        course.title.toLowerCase().includes(query) ||
        course.category.toLowerCase().includes(query) ||
        course.description.toLowerCase().includes(query)
      );
    }

    if (selectedCategory !== 'All Categories') {
      if (isBTGCategory) {
        filtered = [];
      } else {
        filtered = filtered.filter(course => course.category === selectedCategory);
      }
    }

    if (selectedLevel !== 'All Levels') {
      filtered = filtered.filter(course => course.level === selectedLevel);
    }

    if (sortOrder === 'alphabetical') {
      filtered.sort((a, b) => a.title.localeCompare(b.title));
    }

    return filtered;
  }, [departmentCourses, sortOrder, selectedCategory, selectedLevel, searchQuery, isBTGCategory]);

  const getCourseImage = (imageName) => {
    const imageMap = {
      'course1.jpg': course1,
      'course2.jpg': course2,
      'course3.jpg': course3,
      'statistics.png': Statistics,
      'sqlimage.png': Sqlimage,
      'salesforce.png': salesforce,
      'productengineeringandservices.png': productengineeringandservices,
      'H1BSTAFF.png': H1BSTAFF,
      'erp.png': erp,
      'entierpriseandarchitecture.png': entierpriseandarchitecture,
      'datascienceandai.png': datascienceandai,
      'machinelearning.jpg': machinelearning,
      'airflow.jpg': airflow,
      'aws.jpg': aws,
      'azure.jpg': azure,
      'gcp.jpg': gcp,
      'deeplearning.jpg': deeplearning,
      'kafka.jpg': kafka,
      'tableau.jpg': tableau,
      'pyspark.jpg': pyspark,
      'dbt.jpg': dbt,
      'matplotlib.jpg': matplotlib,
      'r.jpg': r,
      'pandas.jpg': pandas,
      'hadoop.jpg': hadoop,
      'automation.jpg': automation,
      'oci.jpg': oci,
      'numpy.jpg': numpy,
      'angular.jpg': angular,
      'springboot.jpg': springboot,
      'react.jpg': react,
      'mulesoft.jpg': mulesoft,
      'kubernetes.png': kubernetes,
      'java.jpg': java,
      'javascript.jpg': javascript,
      'django.jpg': django
    };
    return imageMap[imageName] || course1;
  };

  if (coursesLoading) {
    return (
      <div className="dc-department-courses-page">
        <Link to="/departments" className="dc-back-link">← Back</Link>
        <div className="dc-loading-container">
          <div className="dc-loading-spinner"></div>
          <p>Loading courses...</p>
        </div>
      </div>
    );
  }

  if (coursesError) {
    return (
      <div className="dc-department-courses-page">
        <Link to="/departments" className="dc-back-link">← Back</Link>
        <div className="dc-error-container">
          <p>Error loading courses: {coursesError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dc-department-courses-page">
      <Link to="/departments" className="dc-back-link">← Back</Link>

      {/* Department Information Section */}
      <div className="dc-department-info-section">
        <div className="dc-department-info-card">
          <div className="dc-department-icon-container">
            {currentDepartment && (
              <img
                src={currentDepartment.image}
                alt={departmentName}
                className="dc-department-image"
              />
            )}
          </div>
          <div className="dc-department-info">
            <h1>{departmentName}</h1>
            <p className="dc-department-description">
              {currentDepartment?.description || 'Technical courses designed to enhance skills and knowledge.'}
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filters Section */}
      <div className="dc-search-filters-section">
        <div className="dc-search-container">
          <input
            type="text"
            placeholder="Search courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="dc-search-input"
          />
        </div>
        <div className="dc-filters-container">
          <div className="dc-filter-group">
            <label htmlFor="category-select">Category:</label>
            <select
              id="category-select"
              className="dc-filter-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div className="dc-filter-group">
            <label htmlFor="level-select">Course Level:</label>
            <select
              id="level-select"
              className="dc-filter-select"
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
            >
              {levels.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>
          <ClearFilter onClear={handleClearFilters} />
        </div>
      </div>

      {/* Courses Section */}
      <div className="dc-courses-section">
        {enrolledLoading ? (
          <div className="dc-loading-container">
            <div className="dc-loading-spinner"></div>
            <p>Loading courses...</p>
          </div>
        ) : filteredAndSortedCourses.length > 0 ? (
          <div className="dc-course-grid">
            {filteredAndSortedCourses.map((course, index) => (
              <div className="dc-course-card" key={course.id || index}>
                <img src={getCourseImage(course.image)} alt={course.title} className="dc-course-image" />
                <div className="dc-course-info">
                  <span className="dc-course-category">{course.category}</span>
                  <span className="dc-course-level">{course.level}</span>
                </div>
                <h3 className="dc-course-title">{course.title}</h3>
                <div className="dc-course-details">
                  <span className="dc-course-videos">🎥 Videos: {course.vc}</span>
                  <span className="dc-course-duration">⏱ Duration: {course.td}</span>
                </div>

                <div className="dc-course-enroll-section">
                  {/* If NOT enrolled -> show Enroll Now */}
                  {!enrolledCourses.includes(course.id) ? (
                    <button
                      className="dc-enroll-btn"
                      onClick={() => handleEnroll(course.id)}
                    >
                      Enroll Now
                    </button>
                  ) : (
                    <>
                      {/* If enrolled -> show Continue + Unenroll */}
                      <Link
                        to="/course-videos"
                        state={{
                          department: departmentName,
                          courseId: course.id,
                          courseTitle: course.title
                        }}
                        className="continue-btn"
                      >
                        Continue
                      </Link>
                      <button
                        className="unenroll-btn"
                        onClick={() => handleUnenroll(course.id)}
                      >
                        Unenroll
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : isBTGCategory ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <h3 style={{ marginBottom: '10px', color: '#333' }}>Coming Soon</h3>
            <p style={{ color: '#666', fontSize: '16px', lineHeight: '1.6' }}>
              Videos are scheduled under AI-First initiative. Exciting content is on the way to enhance your learning experience! Stay Tuned!
            </p>
          </div>
        ) : (
          <div className="dc-no-courses-message">
            <p>No courses found</p>
          </div>
        )}
      </div>

      {/* Training Videos Section */}
      <TrainingVideos departmentName={departmentName} />
    </div>
  );
};

export default DepartmentCourses;
